import { useState,useEffect } from "react";
import {
  FileText,
  ShoppingCart,
  Clock,
  DollarSign,
  IndianRupeeIcon,
  Building2,
  Home,
  Settings,
  Bell,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { UserIcon, ChartBarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { decryptData } from "../../../utils/storageCrypto";

function DashboardCard({
  title,
  value,
  description,
  icon: Icon,
  trend,
  actionLabel,
  onAction,
  iconBgColor = "bg-blue-100",
  iconColor = "text-blue-600",
}) {
  return (
    <div className="bg-white rounded-xl shadow-lg p-5 border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
      {/* Top: title and value */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <p className="text-md text-gray-500 font-semibold  tracking-wide">{title}</p>
          <h2 className="text-xl font-semibold text-gray-900 mt-1 truncate">{value ?? "-"}</h2>
        </div>

        <div className={`${iconBgColor} p-3 rounded-full flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-3">{description}</p>

      {/* Trend */}
      {trend && (
        <p
          className={`text-sm font-medium mb-4 ${
            trend.isPositive ? "text-green-600" : "text-red-600"
          } flex items-center gap-1`}
        >
          {trend.isPositive ? "▲" : "▼"} {trend.value}
        </p>
      )}

      {/* Action button */}
      <button
  onClick={onAction}
  className="mt-auto w-full py-2 px-4 rounded-lg bg-gray-100 text-gray-800 font-semibold text-sm shadow-sm hover:bg-red-500 hover:text-white transition-colors flex items-center justify-center gap-2"
>
  {actionLabel}
  <svg
    className="w-3 h-3 text-gray-700"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
</button>

    </div>
  );
}


export default function DashboardStats() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState("overview");
  const [dashboardData, setDashboardData] = useState(/* {
    rfqsCount: 24,
    ordersCount: 156,
    paymentsPending: "$45,230",
    vendorPayments: "$28,540",
    avgResponseTime: "2.4 hrs",
  } */);
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterRole, setFilterRole] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('month'); // week, month, quarter, year
  const enroleId = sessionStorage.getItem('roleId');
  const roleId = decryptData(enroleId)  
  useEffect(() => {
    fetchDashboardData();
  }, [activeView, filterPeriod]);

  loading && <div>Loading...</div>;

  const fetchDashboardData = async () => {
const encryptedToken = sessionStorage.getItem('token');
                const token = decryptData(encryptedToken);
                const encryptedhubId = sessionStorage.getItem('hubId');
                const hubId = decryptData(encryptedhubId);
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}admin-dashboard/get-dashboard-data?hubId=${hubId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token, 
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Dashboard Data:', data);
        setDashboardData(data.data);
        setLoading(false);
      } else {
        console.error('Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };


  const handleAction = (action) => {
    setActiveView(action);
    navigate(`/bb/app/home`,{ state: {activeTab:action } });
    console.log(`Navigating to: ${action}`);
  };

  const encryptedToken = sessionStorage.getItem('token');
                const token = decryptData(encryptedToken);
                const encryptedhubId = sessionStorage.getItem('hubId');
                const hubId = decryptData(encryptedhubId);
    const getUserActivity = process.env.REACT_APP_FORE_APILINK + `/user-activity/get-user-activity?hubId=${hubId}`;
  
    useEffect(() => {
      const fetchUserActivities = async () => {
        try {
          const response = await fetch(getUserActivity, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'x-token': token,
            },
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          console.log(data);
          setActivities(data);
          setLoading(false);
        } catch (err) {
          setError(err.message);
          setLoading(false);
        }
        finally {
          setLoading(false);
        }
      };
  
      fetchUserActivities();
    }, []);

  if (error) return <div className="flex items-center justify-center h-full"><div>Error: {error}</div></div>;


    const formatDateTime = (isoDate) => {
    const dateObj = new Date(isoDate);
    const date = dateObj.toLocaleDateString();
    const time = dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    });
    return `${date} ${time}`;
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedActivities = activities
    .filter(activity => 
      activity.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.activity.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(activity => filterRole ? activity.role === filterRole : true)
    .sort((a, b) => {
      if (a[sortField] < b[sortField]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortField] > b[sortField]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAndSortedActivities.slice(indexOfFirstItem, indexOfLastItem);

  const processActivityText = (text) => {
    const parts = text.split(/"([^"]*)"/);
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        const [label, idInfo] = part.split(/:\((.+)\)/);
        return (
          <span
            key={index}
            className="text-blue-500 cursor-pointer hover:underline"
            onClick={() => handleClickedWord(idInfo, part)}
          >
            {label}
          </span>
        );
      }
      return part;
    });
  };
  
  const handleClickedWord = (idInfo, fullPart) => {
    const [idType, id] = idInfo.split(': ');
    switch (idType.trim()) {
      case 'projectId':
        navigate(`/bb/app/projects/projectdetails`, { state: { projectData:{ _id: id.trim() }} });
        break;
      case 'customerId':
        navigate(`/bb/app/sales/customerdetails`, { state: { customerId: id.trim() } });
        break;
      case 'quotationId':
        navigate(`/bb/app/sales/quotedetails`, { state: { quotationId: id.trim() } });
        break;
      case 'salesorderId':
        navigate(`/bb/app/sales/salesorderdetails`, { state: { salesOrderId: id.trim() } });
        break;
      default:
        console.log("Unknown ID type:", idType, "Full part:", fullPart);
    }
  };

  return (
<div className="h-[80vh] border border-gray-200 rounded-lg bg-white overflow-hidden flex flex-col"> 
       {/* Header */}
{/*       <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-blue-600" />
              <h1 className="text-xl font-semibold">Procurement Hub</h1>
            </div>

            <nav className="hidden md:flex gap-2">
              {[
                { key: "dashboard", label: "Dashboard", icon: Home },
                { key: "rfqs", label: "RFQs", icon: FileText },
                { key: "orders", label: "Orders", icon: ShoppingCart },
                { key: "payments", label: "Payments", icon: DollarSign },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveView(key)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    activeView === key
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <button className="relative p-2 rounded-full hover:bg-gray-100">
              <Bell className="h-5 w-5" />
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-600 text-white text-xs flex items-center justify-center">
                3
              </span>
            </button>

            <button className="p-2 rounded-full hover:bg-gray-100">
              <Settings className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header> */}

      {/* Main */}
<main className="flex-1 overflow-y-auto px-4 pt-4 pb-6 custom-scrollbar">
  <div className="max-w-7xl mx-auto">
          {activeView === "overview" && (
          <>
            <div className="mb-6 flex items-center justify-between">
              <p className="mt-2 text-gray-500 text-lg md:text-lg font-semibold tracking-wide">
                Monitor your procurement activities and key metrics
              </p>
              <div className="flex items-center space-x-2">
                <select
                  value={filterPeriod}
                  onChange={(e) => setFilterPeriod(e.target.value)}
                  className="p-1 border border-gray-300 rounded-md cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option className="cursor-pointer" value="week">Week</option>
                  <option value="month">Month</option>
                  <option value="quarter">Quarter</option>
                  <option value="year">Year</option>
                </select>
                <button
                  onClick={() => window.location.reload()}
                  className="p-2 rounded-full hover:bg-gray-100"
                  title="Refresh"
                >
                  <RefreshCw className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <DashboardCard
                title="Active RFQs"
                value={dashboardData?.rfqsCount}
                description="Requests for quotation in progress"
                icon={FileText}
                trend={{ value: "8% from last month", isPositive: true }}
                actionLabel="View All RFQs"
                onAction={() => handleAction("rfqs")}
                iconBgColor="bg-blue-100"
                iconColor="text-blue-600"
              />

              <DashboardCard
                title="Total Orders"
                value={dashboardData?.ordersCount}
                description="Orders placed this month"
                icon={ShoppingCart}
                trend={{ value: "12% from last month", isPositive: true }}
                actionLabel="Manage Orders"
                onAction={() => handleAction("orders")}
                iconBgColor="bg-green-100"
                iconColor="text-green-600"
              />

              <DashboardCard
                title="Payments Pending"
                value={dashboardData?.paymentsPending}
                description="Outstanding customer payments"
                icon={IndianRupeeIcon}
                trend={{ value: "3% from last week", isPositive: false }}
                actionLabel="Review Payments"
                onAction={() => handleAction("rfqs")}
                iconBgColor="bg-yellow-100"
                 iconColor="text-yellow-600"
              />

              <DashboardCard
                title="Vendor Payments Due"
                value={dashboardData?.vendorPayments}
                description="Payments to be made to vendors"
                icon={IndianRupeeIcon}
                actionLabel="Process Payments"                         
                onAction={() => navigate("/bb/app/vendorpayments/vendorpaymentlist")}
                 iconBgColor="bg-purple-100"
                 iconColor="text-purple-600"
              />

              <DashboardCard
                title="Avg. RFQ Response Time"
                value={dashboardData?.avgResponseTime}
                description="Average time to respond to RFQs"
                icon={Clock}
                trend={{ value: "0.5 hrs faster", isPositive: true }}
                actionLabel="View Analytics"
                //onAction={() => handleAction("analytics")}
                onAction={() => handleAction("overview")}
                 iconBgColor="bg-pink-100"
                  iconColor="text-pink-600"
              />

              {/* Quick Actions */}
{/*               <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg p-6 text-white">
                <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>

                <button
                  onClick={() => handleAction("create-rfq")}
                  className="w-full bg-white text-blue-700 rounded-md px-3 py-2 text-sm font-medium mb-2 hover:bg-gray-100"
                >
                  Create New RFQ
                </button>

                <button
                  onClick={() => handleAction("approve-orders")}
                  className="w-full bg-white text-blue-700 rounded-md px-3 py-2 text-sm font-medium hover:bg-gray-100"
                >
                  Approve Orders
                </button>
              </div> */}



            </div>

            <div className="space-y-6 mt-4">
              {/* User Activity Section */}
              {currentItems.length > 0 ? (
                (roleId === '1' || roleId === '0') && (
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="relative">
                      <div className="absolute inset-0 bg-[#F9F9FB] rounded-t-lg"></div>
                      <div className="relative flex justify-between items-center p-4 border-b">
                        <h2 className="text-lg font-semibold text-gray-800">User Activity</h2>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">
                            Page {currentPage} of {Math.ceil(filteredAndSortedActivities.length / itemsPerPage)}
                          </span>
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm text-blue-500 disabled:text-gray-300 hover:bg-blue-50 rounded transition-colors"
                          >
                            &lt;
                          </button>
                          <button
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            disabled={filteredAndSortedActivities.length <= itemsPerPage || currentPage === Math.ceil(filteredAndSortedActivities.length / itemsPerPage)}
                            className="px-3 py-1 text-sm text-blue-500 disabled:text-gray-300 hover:bg-blue-50 rounded transition-colors"
                          >
                            &gt;
                          </button>
                        </div>
                      </div>
                    </div>
  
                    <div className="p-6">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-2 sm:space-y-0 sm:space-x-4">
                        <input
                          type="text"
                          placeholder="Search activities..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full sm:w-64 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <select
                          value={filterRole}
                          onChange={(e) => setFilterRole(e.target.value)}
                          className="w-full sm:w-48 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">All Roles</option>
                          <option value="Super Admin">Super Admin</option>
                          <option value="Admin">Admin</option>
                          <option value="Marketing Associate">Marketing Associate</option>
                          <option value="Field Associate">Field Associate</option>
                          <option value="Customer">Customer</option>
                        </select>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full text-center border-collapse">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('createdAt')}>
                                Date {sortField === 'createdAt' && (sortDirection === 'asc' ? '▲' : '▼')}
                              </th>
                              <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('username')}>
                                User Name {sortField === 'username' && (sortDirection === 'asc' ? '▲' : '▼')}
                              </th>
                              <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('role')}>
                                Role {sortField === 'role' && (sortDirection === 'asc' ? '▲' : '▼')}
                              </th>
                              <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('activity')}>
                                Activity {sortField === 'activity' && (sortDirection === 'asc' ? '▲' : '▼')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentItems.map((activity, index) => (
                              <tr key={activity.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                                <td className="p-3 text-sm border">{formatDateTime(activity.createdAt)}</td>
                                <td className="p-3 text-sm border font-medium">{activity.username}</td>
                                <td className="p-3 text-sm border">
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                    {activity.role}
                                  </span>
                                </td>
                                <td className="p-3 text-sm border text-left">{processActivityText(activity.activity, activity.data)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8">
                  <div className="text-center text-gray-500">
                    <div className="mb-4">
                      <UserIcon className="mx-auto h-12 w-12 text-gray-300" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">No User Activities</h3>
                    <p className="text-sm">User activities will appear here when available.</p>
                  </div>
                </div>
              )}

            </div>

          </>
        )}
     </div>
      </main>
    </div>
  );
}
